import { getLessonFieldText } from "./lessonLearned";
import { getReportAuthorEmail } from "./reportAuthor";
import {
	getReportAuthorName,
	getReportPlaceParts,
	getReportProjectCode,
	getStreamNameFor,
	getStreamRecordId,
	getStreamRecordLabel,
	getStreamRecordName,
	streamText,
	StreamPlaceReport,
	StreamPlaces,
} from "./reportStream";

// The fields of a lesson the Lessons learned stream reads. A legacy lesson
// (legacy_data) stores its discipline, origin, well and phase as text, and
// only has the date it was created.
export type StreamLesson = StreamPlaceReport & {
	report_number?: unknown;
	alt_report_number?: unknown;
	title?: unknown;
	what_happened?: unknown;
	what_learned?: unknown;
	// The ids of records in the shared lists, as the report form stores them.
	discipline?: unknown;
	origin?: unknown;
	internal_report?: unknown;
	legacy_data?: unknown;
	submitted_on?: string;
	created_on?: string;
	operator?: string;
	primary_author?: string | null;
	author?: string | null;
	// The name an imported lesson stores for an author who may not be a
	// Terra user.
	author_name?: unknown;
};

// A lesson's field as its page shows it: the name, in `field`, of the record
// an id names, or the text a legacy lesson stores. Nothing for an id the
// stream hasn't named, such as one still loading.
export const getLessonRecordText = (
	map: Record<string, any>,
	value: unknown,
	field: string
) =>
	getStreamRecordName(map, value, field) ?? getLessonFieldText(value)?.trim();

// A lesson's value in the Discipline or Origin filter: the record's id, or a
// legacy lesson's text. Text the address can't keep isn't offered
// (getStreamFilterOptions).
export const getLessonFilterValue = (value: unknown) =>
	getStreamRecordId(value) ?? getLessonFieldText(value)?.trim();

// How the Discipline or Origin filter names a value: a record by its name,
// or as unknown until it is found; a legacy lesson's text as it is.
export const getLessonFilterLabel = (
	map: Record<string, any>,
	value: string,
	unknownLabel: string,
	field: string
) =>
	getStreamRecordId(value) === undefined
		? value
		: getStreamRecordLabel(map, value, unknownLabel, field);

// Whether a lesson stays within its tenant, as its page's header says.
export const getLessonScope = (report: StreamLesson) =>
	report.internal_report ? "Internal" : "Global";

// Where a lesson belongs: its rig and country, then its well and phase,
// named from their ids or as a legacy lesson stores them.
export function getLessonPlaceParts(
	report: StreamLesson,
	places: StreamPlaces
) {
	return [
		...getReportPlaceParts(
			{ project: report.project, operation: report.operation },
			places
		),
		getLessonRecordText(places.wellMap, report.well, "well_name"),
		getLessonRecordText(places.phaseMap, report.phase, "phase_name"),
	].filter((part): part is string => part !== undefined);
}

// The line under a lesson's title: its discipline, then whether it is
// internal or global, its origin and where it belongs.
export function getLessonMeta(
	report: StreamLesson,
	places: StreamPlaces,
	disciplineMap: Record<string, any>,
	originMap: Record<string, any>
) {
	const origin = getLessonRecordText(originMap, report.origin, "origin");
	return {
		tag: getLessonRecordText(disciplineMap, report.discipline, "discipline"),
		parts: [
			getLessonScope(report),
			...(origin === undefined ? [] : [origin]),
			...getLessonPlaceParts(report, places),
		],
	};
}

// The date the stream shows and sorts by, as the lesson's page shows it: a
// legacy lesson only has the date it was created.
export const getLessonSubmittedOn = (report: StreamLesson) =>
	report.legacy_data ? report.created_on : report.submitted_on;

// The author as the stream shows them: a known user's name; else the name an
// imported lesson stores, as its page keeps it; else the stored email.
export function getLessonAuthorName(
	report: StreamLesson,
	authorNames: Record<string, string>
) {
	const name = getStreamNameFor(authorNames, getReportAuthorEmail(report));
	if (name) return name;
	const storedName = streamText(report.author_name);
	return storedName === ""
		? getReportAuthorName(report, authorNames)
		: storedName;
}

// What the stream's search looks through for a lesson.
export function getLessonSearchTexts(
	report: StreamLesson,
	places: StreamPlaces,
	disciplineMap: Record<string, any>,
	originMap: Record<string, any>,
	authorNames: Record<string, string>,
	getTenantName: (tenantKey: string) => string | undefined
) {
	const { tag, parts } = getLessonMeta(
		report,
		places,
		disciplineMap,
		originMap
	);
	return [
		report.report_number,
		report.alt_report_number,
		report.title,
		report.what_happened,
		report.what_learned,
		tag,
		...parts,
		getReportProjectCode(report, places),
		getReportAuthorEmail(report),
		getLessonAuthorName(report, authorNames),
		report.operator ? getTenantName(report.operator) : undefined,
	];
}
